import React, { useState, useEffect } from 'react';
import {
  MessageCircle,
  ClipboardList,
  CheckSquare,
  CheckCircle,
  Play,
  Pause,
  RotateCw,
  Club,
  Trash2,
  Plus,
  X,
  Copy,
  Check
} from 'lucide-react';
import ToolDescription from './ToolDescription';
import { getTeamMembers, addTeamMember, removeTeamMember } from '../services/teamService';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

interface RetroColumn {
  title: string;
  items: string[];
}

interface Vote {
  userId: string;
  points: number;
}

interface StoryPointsState {
  users: string[];
  votes: Vote[];
  newUser: string;
  revealed: boolean;
}

interface SimpleStoryPointsState {
  selectedPoint: number | null;
  availablePoints: number[];
  history: number[];
}

interface ActionItem {
  id: string;
  description: string;
  owner: string;
  dueDate: string;
  status: 'not-started' | 'in-progress' | 'blocked' | 'completed';
  createdAt: string;
  meeting: string;
  priority: 'low' | 'medium' | 'high';
}

interface DailyTimer {
  timeLeft: number;
  isRunning: boolean;
  totalTime: number;
}

interface MoodVote {
  member: string;
  mood: 0 | 1 | 2 | 3 | 4;  // 0 = worst, 4 = best
  timestamp: string;
}

interface AgileToolsProps {
  language?: 'en' | 'es';
}

const AgileTools: React.FC<AgileToolsProps> = () => {
  const [activeTab, setActiveTab] = useState('daily');

  
  const [retroColumns, setRetroColumns] = useState<RetroColumn[]>([
    { title: 'What Went Well', items: [] },
    { title: 'What Could Be Improved', items: [] },
    { title: 'Action Items', items: [] }
  ]);
  const [newItem, setNewItem] = useState('');
  const [selectedColumn, setSelectedColumn] = useState(0);
  const [storyPoints, setStoryPoints] = useState<StoryPointsState>({
    users: [],
    votes: [],
    newUser: '',
    revealed: false
  });
  const [simpleMode, setSimpleMode] = useState(false);
  const [simpleStoryPoints, setSimpleStoryPoints] = useState<SimpleStoryPointsState>({
    selectedPoint: null,
    availablePoints: [1, 2, 3, 5, 8, 13, 21, 34],
    history: []
  });
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [newActionItem, setNewActionItem] = useState<Omit<ActionItem, 'id' | 'createdAt'>>({
    description: '',
    owner: '',
    dueDate: '',
    status: 'not-started',
    meeting: '',
    priority: 'medium'
  });
  const [actionItemFilter, setActionItemFilter] = useState<{
    status: string;
    owner: string;
    priority: string;
    meeting: string;
    dueDate: string;
  }>({
    status: 'all',
    owner: 'all',
    priority: 'all',
    meeting: 'all',
    dueDate: 'all'
  });
  const [dailyTimer, setDailyTimer] = useState<DailyTimer>({
    timeLeft: 15 * 60, // 15 minutes in seconds
    isRunning: false,
    totalTime: 15 * 60
  });
  const [clipboardMessage, setClipboardMessage] = useState<{text: string, id: string} | null>(null);
  const [checkedQuestions, setCheckedQuestions] = useState<number[]>([]);
  const [customOwner, setCustomOwner] = useState('');
  const [moodVotes, setMoodVotes] = useState<MoodVote[]>(() => {
    const saved = localStorage.getItem('retro_mood_votes');
    return saved ? JSON.parse(saved) : [];
  });

  const [teamMembers, setTeamMembers] = useState<string[]>([]);
  const [newTeamMember, setNewTeamMember] = useState('');

  const tools = [
    { id: 'daily', name: 'Daily Questions', icon: MessageCircle },
    { id: 'retro', name: 'Retro Board', icon: ClipboardList },
    { id: 'planning', name: 'Story Points', icon: Club },
    { id: 'actions', name: 'Action Items', icon: CheckSquare },
  ];

  const dailyQuestions = [
    'What did you work on yesterday?',
    'What are you working on today?',
    'Are there any blockers?',
    'Do you need help with anything?',
    'Any updates to share?'
  ];

  const addRetroItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (newItem.trim()) {
      const updatedColumns = [...retroColumns];
      updatedColumns[selectedColumn].items.push(newItem.trim());
      setRetroColumns(updatedColumns);
      setNewItem('');
    }
  };

  const addUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (storyPoints.newUser.trim() && !storyPoints.users.includes(storyPoints.newUser.trim())) {
      const updatedMembers = addTeamMember(storyPoints.newUser.trim());
      
      setStoryPoints(prev => ({
        ...prev,
        users: updatedMembers,
        newUser: ''
      }));
    }
  };

  const handleVote = (userId: string, points: number) => {
    setStoryPoints(prev => ({
      ...prev,
      votes: [...prev.votes.filter(v => v.userId !== userId), { userId, points }]
    }));
  };

  const resetVoting = () => {
    setStoryPoints(prev => ({
      ...prev,
      votes: [],
      revealed: false
    }));
  };

  const removeUser = (userId: string) => {
    const updatedMembers = removeTeamMember(userId);
    
    setStoryPoints(prev => ({
      ...prev,
      users: updatedMembers,
      votes: prev.votes.filter(v => v.userId !== userId)
    }));
  };

  const addActionItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (newActionItem.description.trim()) {
      if (newActionItem.owner && 
          newActionItem.owner !== 'other' && 
          !getTeamMembers().includes(newActionItem.owner)) {
        addTeamMember(newActionItem.owner);
      }

      const newItem: ActionItem = {
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        ...newActionItem,
        owner: newActionItem.owner === 'other' ? customOwner : newActionItem.owner
      };

      const updatedItems = [...actionItems, newItem];
      setActionItems(updatedItems);
      
      if (newActionItem.owner === 'other' && customOwner.trim()) {
        addTeamMember(customOwner);
      }

      setNewActionItem({
        description: '',
        owner: '',
        dueDate: '',
        status: 'not-started',
        meeting: newActionItem.meeting,
        priority: 'medium'
      });
      
      setCustomOwner('');
    }
  };

  const updateActionItemStatus = (id: string, status: ActionItem['status']) => {
    const updatedItems = actionItems.map(item => 
      item.id === id ? { ...item, status } : item
    );
    setActionItems(updatedItems);
  };

  const deleteActionItem = (id: string) => {
    const updatedItems = actionItems.filter(item => item.id !== id);
    setActionItems(updatedItems);
  };

  const filteredActionItems = actionItems.filter(item => {
    if (actionItemFilter.status !== 'all' && item.status !== actionItemFilter.status) {
      return false;
    }
    if (actionItemFilter.owner !== 'all' && item.owner !== actionItemFilter.owner) {
      return false;
    }
    if (actionItemFilter.priority !== 'all' && item.priority !== actionItemFilter.priority) {
      return false;
    }
    if (actionItemFilter.meeting !== 'all' && item.meeting !== actionItemFilter.meeting) {
      return false;
    }
    if (actionItemFilter.dueDate !== 'all') {
      if (actionItemFilter.dueDate === 'overdue') {
        const dueDate = new Date(item.dueDate);
        const today = new Date();
        if (!item.dueDate || dueDate >= today) return false;
      } else if (actionItemFilter.dueDate === 'upcoming') {
        const dueDate = new Date(item.dueDate);
        const today = new Date();
        const nextWeek = new Date();
        nextWeek.setDate(today.getDate() + 7);
        if (!item.dueDate || dueDate < today || dueDate > nextWeek) return false;
      } else if (actionItemFilter.dueDate === 'future') {
        const dueDate = new Date(item.dueDate);
        const nextWeek = new Date();
        nextWeek.setDate(new Date().getDate() + 7);
        if (!item.dueDate || dueDate <= nextWeek) return false;
      } else if (actionItemFilter.dueDate === 'none') {
        if (item.dueDate) return false;
      }
    }
    return true;
  });

  const getUniqueOwners = () => {
    const owners = new Set<string>();
    actionItems.forEach(item => {
      if (item.owner) owners.add(item.owner);
    });
    return Array.from(owners);
  };

  const getUniqueMeetings = () => {
    const meetings = new Set<string>();
    actionItems.forEach(item => {
      if (item.meeting) meetings.add(item.meeting);
    });
    return Array.from(meetings);
  };

  const selectSimpleStoryPoint = (points: number) => {
    setSimpleStoryPoints(prev => ({
      ...prev,
      selectedPoint: points,
      history: [...prev.history, points]
    }));
  };

  const resetSimpleStoryPoint = () => {
    setSimpleStoryPoints(prev => ({
      ...prev,
      selectedPoint: null,
      history: []
    }));
  };

  // Add timer effect
  useEffect(() => {
    let timer: number;

    if (dailyTimer.isRunning && dailyTimer.timeLeft > 0) {
      timer = window.setInterval(() => {
        setDailyTimer(prev => ({
          ...prev,
          timeLeft: prev.timeLeft - 1
        }));
      }, 1000);
    }

    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, [dailyTimer.isRunning, dailyTimer.timeLeft]);

  // Add timer functions
  const toggleDailyTimer = () => {
    setDailyTimer(prev => ({
      ...prev,
      isRunning: !prev.isRunning
    }));
  };

  const resetDailyTimer = () => {
    setDailyTimer(prev => ({
      ...prev,
      timeLeft: prev.totalTime,
      isRunning: false
    }));
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Función para copiar al portapapeles
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        // Mostrar mensaje de éxito
        setClipboardMessage({ text, id });
        
        // Ocultar el mensaje después de 2 segundos
        setTimeout(() => {
          setClipboardMessage(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Error copying to clipboard: ', err);
      });
  };

  const toggleQuestionChecked = (index: number) => {
    setCheckedQuestions(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  
  const resetCheckedQuestions = () => {
    setCheckedQuestions([]);
  };

  // Listen for team members updates from other components
  useEffect(() => {
    const handleTeamMembersUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.members) {
        setStoryPoints(prev => ({
          ...prev,
          users: customEvent.detail.members
        }));
      }
    };
    
    window.addEventListener('team-members-updated', handleTeamMembersUpdate);
    
    return () => {
      window.removeEventListener('team-members-updated', handleTeamMembersUpdate);
    };
  }, []);

  // Load data from localStorage on component mount
  useEffect(() => {
    // Load team members
    const savedTeamMembers = getTeamMembers();
    setStoryPoints(prev => ({
      ...prev,
      users: savedTeamMembers
    }));

    // Load action items
    const savedActionItems = localStorage.getItem('app_action_items');
    if (savedActionItems) {
      try {
        setActionItems(JSON.parse(savedActionItems));
      } catch (e) {
        console.error('Error loading action items:', e);
      }
    }
  }, []);

  // Save action items to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('app_action_items', JSON.stringify(actionItems));
  }, [actionItems]);

  // Save mood votes to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('retro_mood_votes', JSON.stringify(moodVotes));
  }, [moodVotes]);

  const handleMoodVote = (member: string, mood: MoodVote['mood']) => {
    setMoodVotes(prev => {
      const filtered = prev.filter(vote => vote.member !== member);
      return [...filtered, { member, mood, timestamp: new Date().toISOString() }];
    });
  };

  const getMoodCount = (mood: MoodVote['mood']): number => {
    return moodVotes.filter(vote => vote.mood === mood).length;
  };

  const getMemberMood = (member: string): MoodVote['mood'] | null => {
    const vote = moodVotes.find(v => v.member === member);
    return vote ? vote.mood : null;
  };

  // Función para limpiar todo el tablero de retrospectiva
  const clearRetroBoard = () => {
    setRetroColumns([
      { title: 'What Went Well', items: [] },
      { title: 'What Could Be Improved', items: [] },
      { title: 'Action Items', items: [] }
    ]);
  };

  // Cargar miembros del equipo al iniciar
  useEffect(() => {
    setTeamMembers(getTeamMembers());

    // Escuchar actualizaciones de los miembros del equipo
    const handleTeamMembersUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.members) {
        setTeamMembers(customEvent.detail.members);
      } else {
        setTeamMembers(getTeamMembers());
      }
    };

    window.addEventListener('team-members-updated', handleTeamMembersUpdate);
    return () => {
      window.removeEventListener('team-members-updated', handleTeamMembersUpdate);
    };
  }, []);

  // Añadir un nuevo miembro de equipo
  const handleAddTeamMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTeamMember.trim()) {
      const updatedMembers = addTeamMember(newTeamMember.trim());
      setTeamMembers(updatedMembers);
      setNewTeamMember('');
    }
  };

  // Eliminar un miembro del equipo
  const handleRemoveTeamMember = (member: string) => {
    const updatedMembers = removeTeamMember(member);
    setTeamMembers(updatedMembers);
  };

  const copyActionItemsToMarkdown = () => {
    // Preparar el contenido en formato Markdown
    let markdownContent = "# Action Items\n\n";
    
    // Agrupar los elementos por estado
    const notStarted = actionItems.filter(item => item.status === 'not-started');
    const inProgress = actionItems.filter(item => item.status === 'in-progress');
    const blocked = actionItems.filter(item => item.status === 'blocked');
    const completed = actionItems.filter(item => item.status === 'completed');
    
    // Formatear sección de Not Started
    if (notStarted.length > 0) {
      markdownContent += "## 🔘 Not Started\n\n";
      notStarted.forEach(item => {
        const prioritySymbol = item.priority === 'high' ? '🔴' : item.priority === 'medium' ? '🟠' : '🟢';
        markdownContent += `- ${prioritySymbol} **${item.description}**\n`;
        markdownContent += `  - **Owner:** ${item.owner}\n`;
        if (item.dueDate) {
          markdownContent += `  - **Due Date:** ${new Date(item.dueDate).toLocaleDateString()}\n`;
        }
        markdownContent += `  - **Meeting:** ${item.meeting}\n\n`;
      });
    }
    
    // Formatear sección de In Progress
    if (inProgress.length > 0) {
      markdownContent += "## ▶️ In Progress\n\n";
      inProgress.forEach(item => {
        const prioritySymbol = item.priority === 'high' ? '🔴' : item.priority === 'medium' ? '🟠' : '🟢';
        markdownContent += `- ${prioritySymbol} **${item.description}**\n`;
        markdownContent += `  - **Owner:** ${item.owner}\n`;
        if (item.dueDate) {
          markdownContent += `  - **Due Date:** ${new Date(item.dueDate).toLocaleDateString()}\n`;
        }
        markdownContent += `  - **Meeting:** ${item.meeting}\n\n`;
      });
    }
    
    // Formatear sección de Blocked
    if (blocked.length > 0) {
      markdownContent += "## ⛔ Blocked\n\n";
      blocked.forEach(item => {
        const prioritySymbol = item.priority === 'high' ? '🔴' : item.priority === 'medium' ? '🟠' : '🟢';
        markdownContent += `- ${prioritySymbol} **${item.description}**\n`;
        markdownContent += `  - **Owner:** ${item.owner}\n`;
        if (item.dueDate) {
          markdownContent += `  - **Due Date:** ${new Date(item.dueDate).toLocaleDateString()}\n`;
        }
        markdownContent += `  - **Meeting:** ${item.meeting}\n\n`;
      });
    }
    
    // Formatear sección de Completed
    if (completed.length > 0) {
      markdownContent += "## ✅ Completed\n\n";
      completed.forEach(item => {
        const prioritySymbol = item.priority === 'high' ? '🔴' : item.priority === 'medium' ? '🟠' : '🟢';
        markdownContent += `- ${prioritySymbol} **${item.description}**\n`;
        markdownContent += `  - **Owner:** ${item.owner}\n`;
        if (item.dueDate) {
          markdownContent += `  - **Due Date:** ${new Date(item.dueDate).toLocaleDateString()}\n`;
        }
        markdownContent += `  - **Meeting:** ${item.meeting}\n\n`;
      });
    }
    
    // Copiar al portapapeles
    navigator.clipboard.writeText(markdownContent)
      .then(() => {
        setClipboardMessage({
          id: 'action-items-markdown',
          text: 'Copied to clipboard!'
        });
        setTimeout(() => {
          setClipboardMessage(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy to clipboard: ', err);
      });
  };

  // Añadir esta función para manejar el final del drag and drop
  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    
    // Si no hay destino (se soltó fuera de una zona válida) o no cambió de posición
    if (!destination || 
        (source.droppableId === destination.droppableId && 
         source.index === destination.index)) {
      return;
    }
    
    // Obtener el nuevo estado basado en el id del droppable
    const newStatus = destination.droppableId as ActionItem['status'];
    
    // Actualizar el estado del item
    updateActionItemStatus(draggableId, newStatus);
  };

  return (
    <div className="space-y-6">
      <ToolDescription 
        title="Agile Tools"
        description="A comprehensive suite of tools designed specifically for agile development teams. These tools support your agile ceremonies, retrospectives, and team collaboration, helping you implement agile methodologies effectively."
        benefits={[
          "Enhance team collaboration and communication",
          "Streamline agile ceremonies and meetings",
          "Improve retrospective quality with structured feedback",
          "Track action items and team progress",
          "Foster continuous improvement culture"
        ]}
        howToUse="Choose from the available tools using the tabs below. Each tool is designed to support different aspects of agile development - from daily standups to retrospectives. Perfect for Scrum Masters, Product Owners, and agile teams of any size."
      />

      <div className="bg-gray-200 dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            Agile Tools
          </h2>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => setActiveTab(tool.id)}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg
                ${
                  activeTab === tool.id
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                    : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
                }
              `}
            >
              <tool.icon size={20} />
              <span>{tool.name}</span>
            </button>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg p-6">
          {activeTab === 'daily' && (
            <div className="space-y-6">
              {/* Daily Timer */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-6">
                <div className="text-center space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Daily Meeting Timer
                  </h3>
                  <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                    {formatTime(dailyTimer.timeLeft)}
                  </div>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={toggleDailyTimer}
                      className={`
                        flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
                        ${dailyTimer.isRunning
                          ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200'
                          : 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                        }
                      `}
                    >
                      {dailyTimer.isRunning ? <Pause size={20} /> : <Play size={20} />}
                      <span>{dailyTimer.isRunning ? 'Pause' : 'Start'}</span>
                    </button>
                    <button
                      onClick={resetDailyTimer}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                    >
                      <RotateCw size={20} />
                      <span>Reset</span>
                    </button>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Keep your daily meetings within 15 minutes
                  </div>
                </div>

                {/* Daily Questions */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Daily Questions</h3>
                    {checkedQuestions.length > 0 && (
                      <button
                        onClick={resetCheckedQuestions}
                        className="text-xs bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 px-2 py-1 rounded-md text-gray-700 dark:text-gray-300"
                      >
                        Reset Checks
                      </button>
                    )}
                  </div>
                  {dailyQuestions.map((question, index) => (
                    <div 
                      key={index} 
                      className={`p-4 bg-white dark:bg-gray-800 rounded-lg flex items-center ${
                        checkedQuestions.includes(index) ? 'border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <button
                        onClick={() => toggleQuestionChecked(index)}
                        className={`mr-3 p-1 rounded-full ${
                          checkedQuestions.includes(index)
                            ? 'text-blue-600 bg-blue-100 dark:bg-green-900/20'
                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-green-900/20'
                        }`}
                        title={checkedQuestions.includes(index) ? "Uncheck question" : "Check question"}
                      >
                        <CheckCircle size={20} />
                      </button>
                      <p className="text-lg text-gray-800 dark:text-gray-200">
                        {question}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'retro' && (
            <div className="space-y-4">
              {/* Formulario para añadir items */}
              <form onSubmit={addRetroItem} className="flex gap-2 mb-4">
                <select
                  value={selectedColumn}
                  onChange={(e) => setSelectedColumn(Number(e.target.value))}
                  className="px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                >
                  {retroColumns.map((column, index) => (
                    <option key={index} value={index}>{column.title}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  placeholder="Add item"
                  className="flex-1 px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                />
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
                >
                  Add Item
                </button>
              </form>
              
              {/* Botón para limpiar el tablero */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={clearRetroBoard}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  title="Clear all items from the retro board"
                >
                  <Trash2 size={18} />
                  <span>Clear Board</span>
                </button>
              </div>

              {/* Formulario para gestionar miembros del equipo */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4">
                <h3 className="font-bold mb-4 text-gray-800 dark:text-white">Team Members</h3>
                
                <form onSubmit={handleAddTeamMember} className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newTeamMember}
                    onChange={(e) => setNewTeamMember(e.target.value)}
                    placeholder="Add team member"
                    className="flex-1 px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  />
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <Plus size={18} />
                    <span>Add</span>
                  </button>
                </form>

                <div className="flex flex-wrap gap-2">
                  {teamMembers.map((member) => (
                    <div 
                      key={member}
                      className="bg-gray-200 dark:bg-gray-700 px-3 py-1 rounded-full flex items-center gap-2"
                    >
                      <span className="text-gray-800 dark:text-gray-200">{member}</span>
                      <button 
                        onClick={() => handleRemoveTeamMember(member)}
                        className="text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        title="Remove team member"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  {teamMembers.length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm italic">
                      No team members added yet. Add team members to assign tasks and track participation.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {retroColumns.map((column, columnIndex) => (
                  <div key={columnIndex} className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <h3 className="font-bold mb-4 text-gray-800 dark:text-white">{column.title}</h3>
                    <div className="space-y-2">
                      {column.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="p-2 bg-gray-100 dark:bg-gray-700 rounded">
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Mood Tracker */}
              <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg p-4">
                <h3 className="font-bold text-lg mb-4 text-gray-800 dark:text-white">Team Mood Tracker</h3>
                
                {/* Mood Statistics */}
                <div className="grid grid-cols-5 gap-4 mb-6">
                  {[
                    { emoji: '😢', label: 'Very Bad' },
                    { emoji: '😕', label: 'Bad' },
                    { emoji: '😐', label: 'Neutral' },
                    { emoji: '🙂', label: 'Good' },
                    { emoji: '😄', label: 'Very Good' }
                  ].map((mood, index) => (
                    <div key={index} className="text-center">
                      <div className="text-3xl mb-2">{mood.emoji}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{mood.label}</div>
                      <div className="text-lg font-bold text-gray-800 dark:text-white">
                        {getMoodCount(index as MoodVote['mood'])}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Individual Voting */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-2">Vote Your Mood</h4>
                  <div className="grid gap-4">
                    {getTeamMembers().map((member) => (
                      <div key={member} className="flex items-center justify-between bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
                        <span className="font-medium text-gray-800 dark:text-white">{member}</span>
                        <div className="flex gap-3">
                          {[
                            { emoji: '😢', mood: 0 },
                            { emoji: '😕', mood: 1 },
                            { emoji: '😐', mood: 2 },
                            { emoji: '🙂', mood: 3 },
                            { emoji: '😄', mood: 4 }
                          ].map(({ emoji, mood }) => (
                            <button
                              key={mood}
                              onClick={() => handleMoodVote(member, mood as MoodVote['mood'])}
                              className={`text-2xl p-1 rounded-lg transition-transform hover:scale-110 ${
                                getMemberMood(member) === mood ? 'bg-blue-100 dark:bg-blue-900 scale-110' : ''
                              }`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'planning' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                  Story Points
                </h3>
                <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setSimpleMode(false)}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      !simpleMode 
                        ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-white shadow'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
                    }`}
                  >
                    Team Mode
                  </button>
                  <button
                    onClick={() => setSimpleMode(true)}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      simpleMode 
                        ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-white shadow'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
                    }`}
                  >
                    Simple Mode
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Team Mode Panel */}
                <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm ${simpleMode ? 'opacity-50' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white">
                      Team Mode
                    </h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      !simpleMode ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {!simpleMode ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Add team members and collect story point estimates from everyone
                  </p>

                  <div className={`space-y-4 ${simpleMode ? 'pointer-events-none' : ''}`}>
                    <form onSubmit={addUser} className="flex gap-2">
                      <input
                        type="text"
                        value={storyPoints.newUser}
                        onChange={(e) => setStoryPoints(prev => ({ ...prev, newUser: e.target.value }))}
                        placeholder="Add team member"
                        className="flex-1 px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                        disabled={simpleMode}
                      />
                      <button
                        type="submit"
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={simpleMode}
                      >
                        Add Member
                      </button>
                    </form>

                    {storyPoints.users.length > 0 && (
                      <div className="space-y-4">
                        {storyPoints.users.map((user) => (
                          <div key={user} className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="font-semibold text-gray-800 dark:text-white">{user}</h3>
                              <button
                                onClick={() => removeUser(user)}
                                className="text-red-500 hover:text-red-600"
                                disabled={simpleMode}
                              >
                                ×
                              </button>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              {[1, 2, 3, 5, 8, 13, 21, 34].map((points) => (
                                <button
                                  key={points}
                                  onClick={() => handleVote(user, points)}
                                  className={`p-2 text-sm font-bold rounded-lg transition-colors
                                    ${storyPoints.votes.find(v => v.userId === user)?.points === points
                                      ? 'bg-blue-500 text-white'
                                      : 'bg-gray-200 dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                                    }`}
                                  disabled={simpleMode}
                                >
                                  {points}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}

                        <div className="flex justify-between items-center">
                          <button
                            onClick={resetVoting}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={simpleMode}
                          >
                            Reset
                          </button>
                          <button
                            onClick={() => setStoryPoints(prev => ({ ...prev, revealed: true }))}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={simpleMode}
                          >
                            Reveal
                          </button>
                        </div>

                        {storyPoints.revealed && storyPoints.votes.length > 0 && (
                          <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <h3 className="font-bold mb-2 text-gray-800 dark:text-white">Results</h3>
                            <div className="space-y-2">
                              {storyPoints.votes.map((vote) => (
                                <div key={vote.userId} className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">{vote.userId}:</span>
                                  <span className="font-semibold">{vote.points}</span>
                                </div>
                              ))}
                              <div className="border-t pt-2 mt-2">
                                <div className="flex justify-between font-bold">
                                  <span>Average</span>
                                  <div className="flex items-center">
                                    <span>
                                      {(storyPoints.votes.reduce((acc, v) => acc + v.points, 0) / storyPoints.votes.length).toFixed(1)}
                                    </span>
                                    <button
                                      onClick={() => copyToClipboard(
                                        (storyPoints.votes.reduce((acc, v) => acc + v.points, 0) / storyPoints.votes.length).toFixed(1),
                                        'team-average'
                                      )}
                                      className="ml-2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 relative"
                                      title="Copy to clipboard"
                                    >
                                      {clipboardMessage && clipboardMessage.id === 'team-average' ? (
                                        <div className="absolute -top-8 -left-12 bg-green-100 text-green-800 text-xs px-2 py-1 rounded whitespace-nowrap flex items-center">
                                          <Check size={12} className="mr-1" />
                                          Copied!
                                        </div>
                                      ) : null}
                                      <Copy size={14} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Simple Mode Panel */}
                <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm ${!simpleMode ? 'opacity-50' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white">
                      Simple Mode
                    </h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      simpleMode ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {simpleMode ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Quick and simple story point estimation
                  </p>

                  <div className={`space-y-6 ${!simpleMode ? 'pointer-events-none' : ''}`}>
                    <div className="grid grid-cols-4 gap-4">
                      {simpleStoryPoints.availablePoints.map((points) => (
                        <button
                          key={points}
                          onClick={() => selectSimpleStoryPoint(points)}
                          className={`
                            p-4 text-xl font-bold rounded-lg transition-colors
                            ${simpleStoryPoints.selectedPoint === points
                              ? 'bg-blue-500 text-white transform scale-110 shadow-lg'
                              : 'bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:scale-105'
                            }
                          `}
                          disabled={!simpleMode}
                        >
                          {points}
                        </button>
                      ))}
                    </div>
                    
                    <div className="space-y-4">
                      {simpleStoryPoints.selectedPoint !== null && (
                        <div className="flex flex-col gap-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                          <div className="flex justify-between items-center">
                            <div className="text-xl font-semibold text-gray-800 dark:text-white">
                              Latest estimate: <span className="text-blue-500 dark:text-blue-400">{simpleStoryPoints.selectedPoint}</span>
                            </div>
                            <button
                              onClick={resetSimpleStoryPoint}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!simpleMode}
                            >
                              Reset
                            </button>
                          </div>
                          
                          {simpleStoryPoints.history.length > 0 && (
                            <div className="border-t pt-4 space-y-2">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Sum</div>
                                  <div className="text-lg font-semibold text-gray-800 dark:text-white">
                                    {simpleStoryPoints.history.reduce((a, b) => a + b, 0)}
                                  </div>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                                  <div className="text-sm text-gray-600 dark:text-gray-400">Average</div>
                                  <div className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                                    {(simpleStoryPoints.history.reduce((a, b) => a + b, 0) / simpleStoryPoints.history.length).toFixed(1)}
                                    <button
                                      onClick={() => copyToClipboard(
                                        (simpleStoryPoints.history.reduce((a, b) => a + b, 0) / simpleStoryPoints.history.length).toFixed(1),
                                        'simple-average'
                                      )}
                                      className="ml-2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 relative"
                                      title="Copy to clipboard"
                                    >
                                      {clipboardMessage && clipboardMessage.id === 'simple-average' ? (
                                        <div className="absolute -top-8 -left-12 bg-green-100 text-green-800 text-xs px-2 py-1 rounded whitespace-nowrap flex items-center">
                                          <Check size={12} className="mr-1" />
                                          Copied!
                                        </div>
                                      ) : null}
                                      <Copy size={14} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                History ({simpleStoryPoints.history.length} estimates): 
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {simpleStoryPoints.history.map((point, index) => (
                                    <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded">
                                      {point}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-6">
              <form onSubmit={addActionItem} className="bg-white dark:bg-gray-800 rounded-lg p-4 space-y-4">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                  Add New Action Item
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={newActionItem.description}
                      onChange={(e) => setNewActionItem({...newActionItem, description: e.target.value})}
                      placeholder="What needs to be done?"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:bg-gray-800 dark:border-gray-600 text-gray-800 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Owner
                    </label>
                    <div className="space-y-2">
                      <select
                        value={newActionItem.owner}
                        onChange={(e) => setNewActionItem({...newActionItem, owner: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:bg-gray-800 dark:border-gray-600 text-gray-800 dark:text-white"
                      >
                        <option value="">Select an owner</option>
                        {getTeamMembers().map(member => (
                          <option key={member} value={member}>{member}</option>
                        ))}
                        <option value="other">Add new team member</option>
                      </select>
                      
                      {newActionItem.owner === 'other' && (
                        <input
                          type="text"
                          value={customOwner}
                          onChange={(e) => setCustomOwner(e.target.value)}
                          placeholder="Enter new team member name"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:bg-gray-800 dark:border-gray-600 text-gray-800 dark:text-white"
                          required
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={newActionItem.dueDate}
                      onChange={(e) => setNewActionItem({...newActionItem, dueDate: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:bg-gray-800 dark:border-gray-600 text-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Meeting Type
                    </label>
                    <select
                      value={newActionItem.meeting}
                      onChange={(e) => setNewActionItem({...newActionItem, meeting: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:bg-gray-800 dark:border-gray-600 text-gray-800 dark:text-white"
                    >
                      <option value="">Select a meeting</option>
                      <option value="Daily Standup">Daily Standup</option>
                      <option value="Sprint Planning">Sprint Planning</option>
                      <option value="Retrospective">Retrospective</option>
                      <option value="Refinement">Refinement</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Priority
                    </label>
                    <select
                      value={newActionItem.priority}
                      onChange={(e) => setNewActionItem({...newActionItem, priority: e.target.value as 'low' | 'medium' | 'high'})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:bg-gray-800 dark:border-gray-600 text-gray-800 dark:text-white"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
                    >
                      Add Action Item
                    </button>
                  </div>
                </div>
              </form>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                    Action Items ({filteredActionItems.length})
                  </h3>
                  
                  <button
                    onClick={copyActionItemsToMarkdown}
                    className="flex items-center bg-green-100 hover:bg-green-200 dark:bg-green-800 dark:hover:bg-green-700 text-green-800 dark:text-green-100 px-3 py-1.5 rounded-lg text-sm relative"
                    title="Copy action items in Markdown format"
                  >
                    <Copy size={16} className="mr-1.5" />
                    <span>Copy to MD</span>
                    {clipboardMessage && clipboardMessage.id === 'action-items-markdown' && (
                      <div className="absolute -top-8 right-0 bg-green-100 text-green-800 text-xs px-2 py-1 rounded whitespace-nowrap flex items-center">
                        <Check size={12} className="mr-1" />
                        Copied!
                      </div>
                    )}
                  </button>
                </div>
                
                {/* Comprehensive Filter Panel */}
                <div className="w-full bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">Filters</h4>
                    <button
                      onClick={() => setActionItemFilter({
                        status: 'all',
                        owner: 'all',
                        priority: 'all',
                        meeting: 'all',
                        dueDate: 'all'
                      })}
                      className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                    >
                      Clear All
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Status</label>
                      <select
                        value={actionItemFilter.status}
                        onChange={(e) => setActionItemFilter({...actionItemFilter, status: e.target.value})}
                        className="w-full px-2 py-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      >
                        <option value="all">All Statuses</option>
                        <option value="not-started">Not Started</option>
                        <option value="in-progress">In Progress</option>
                        <option value="blocked">Blocked</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Owner</label>
                      <select
                        value={actionItemFilter.owner}
                        onChange={(e) => setActionItemFilter({...actionItemFilter, owner: e.target.value})}
                        className="w-full px-2 py-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      >
                        <option value="all">All Owners</option>
                        {getUniqueOwners().map(owner => (
                          <option key={owner} value={owner}>{owner}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Priority</label>
                      <select
                        value={actionItemFilter.priority}
                        onChange={(e) => setActionItemFilter({...actionItemFilter, priority: e.target.value})}
                        className="w-full px-2 py-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      >
                        <option value="all">All Priorities</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Meeting</label>
                      <select
                        value={actionItemFilter.meeting}
                        onChange={(e) => setActionItemFilter({...actionItemFilter, meeting: e.target.value})}
                        className="w-full px-2 py-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      >
                        <option value="all">All Meetings</option>
                        {getUniqueMeetings().map(meeting => (
                          <option key={meeting} value={meeting}>{meeting}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Due Date</label>
                      <select
                        value={actionItemFilter.dueDate}
                        onChange={(e) => setActionItemFilter({...actionItemFilter, dueDate: e.target.value})}
                        className="w-full px-2 py-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      >
                        <option value="all">All Dates</option>
                        <option value="overdue">Overdue</option>
                        <option value="upcoming">Next 7 Days</option>
                        <option value="future">Future (&gt;7 days)</option>
                        <option value="none">No Due Date</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* Trello-like Board */}
                {filteredActionItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No action items found with the current filters. Try clearing some filters or add new items.
                  </div>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 overflow-x-auto min-h-[60vh]">
                      {/* Not Started Column */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col h-full">
                        <div className="flex items-center justify-between mb-3 pb-2 border-b dark:border-gray-600">
                          <h4 className="font-medium flex items-center">
                            <span className="w-3 h-3 bg-gray-400 rounded-full mr-2"></span>
                            Not Started
                            <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                              {filteredActionItems.filter(item => item.status === 'not-started').length}
                            </span>
                          </h4>
                        </div>
                        <Droppable droppableId="not-started">
                          {(provided) => (
                            <div 
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="space-y-3 flex-1 overflow-y-auto"
                            >
                              {filteredActionItems
                                .filter(item => item.status === 'not-started')
                                .map((item, index) => (
                                  <Draggable 
                                    key={item.id} 
                                    draggableId={item.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`
                                          p-3 rounded-lg shadow-sm bg-white dark:bg-gray-800
                                          ${item.priority === 'high' ? 'ring-2 ring-red-200 dark:ring-red-900' : ''}
                                          ${snapshot.isDragging ? 'shadow-lg opacity-80' : ''}
                                        `}
                                      >
                                        <div className="space-y-2">
                                          <div className="flex justify-between items-start">
                                            <p className="font-medium text-gray-800 dark:text-white">
                                              {item.description}
                                            </p>
                                            <select
                                              value={item.status}
                                              onChange={(e) => updateActionItemStatus(item.id, e.target.value as ActionItem['status'])}
                                              className="text-xs ml-1 px-1 py-0.5 rounded border bg-gray-50 dark:bg-gray-700"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <option value="not-started">Not Started</option>
                                              <option value="in-progress">In Progress</option>
                                              <option value="blocked">Blocked</option>
                                              <option value="completed">Completed</option>
                                            </select>
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {item.priority && (
                                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                item.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                                item.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                              }`}>
                                                {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                                              </span>
                                            )}
                                            {item.owner && (
                                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                {item.owner}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex flex-wrap justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                                            {item.dueDate && (
                                              <span>Due: {new Date(item.dueDate).toLocaleDateString()}</span>
                                            )}
                                            <button
                                              onClick={() => deleteActionItem(item.id)}
                                              className="text-red-500 hover:text-red-700"
                                              title="Delete"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                      
                      {/* In Progress Column */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col h-full">
                        <div className="flex items-center justify-between mb-3 pb-2 border-b dark:border-gray-600">
                          <h4 className="font-medium flex items-center">
                            <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                            In Progress
                            <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                              {filteredActionItems.filter(item => item.status === 'in-progress').length}
                            </span>
                          </h4>
                        </div>
                        <Droppable droppableId="in-progress">
                          {(provided) => (
                            <div 
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="space-y-3 flex-1 overflow-y-auto"
                            >
                              {filteredActionItems
                                .filter(item => item.status === 'in-progress')
                                .map((item, index) => (
                                  <Draggable 
                                    key={item.id} 
                                    draggableId={item.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`
                                          p-3 rounded-lg shadow-sm bg-white dark:bg-gray-800
                                          ${item.priority === 'high' ? 'ring-2 ring-red-200 dark:ring-red-900' : ''}
                                          ${snapshot.isDragging ? 'shadow-lg opacity-80' : ''}
                                        `}
                                      >
                                        <div className="space-y-2">
                                          <div className="flex justify-between items-start">
                                            <p className="font-medium text-gray-800 dark:text-white">
                                              {item.description}
                                            </p>
                                            <select
                                              value={item.status}
                                              onChange={(e) => updateActionItemStatus(item.id, e.target.value as ActionItem['status'])}
                                              className="text-xs ml-1 px-1 py-0.5 rounded border bg-gray-50 dark:bg-gray-700"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <option value="not-started">Not Started</option>
                                              <option value="in-progress">In Progress</option>
                                              <option value="blocked">Blocked</option>
                                              <option value="completed">Completed</option>
                                            </select>
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {item.priority && (
                                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                item.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                                item.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                              }`}>
                                                {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                                              </span>
                                            )}
                                            {item.owner && (
                                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                {item.owner}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex flex-wrap justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                                            {item.dueDate && (
                                              <span>Due: {new Date(item.dueDate).toLocaleDateString()}</span>
                                            )}
                                            <button
                                              onClick={() => deleteActionItem(item.id)}
                                              className="text-red-500 hover:text-red-700"
                                              title="Delete"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                      
                      {/* Blocked Column */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col h-full">
                        <div className="flex items-center justify-between mb-3 pb-2 border-b dark:border-gray-600">
                          <h4 className="font-medium flex items-center">
                            <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                            Blocked
                            <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                              {filteredActionItems.filter(item => item.status === 'blocked').length}
                            </span>
                          </h4>
                        </div>
                        <Droppable droppableId="blocked">
                          {(provided) => (
                            <div 
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="space-y-3 flex-1 overflow-y-auto"
                            >
                              {filteredActionItems
                                .filter(item => item.status === 'blocked')
                                .map((item, index) => (
                                  <Draggable 
                                    key={item.id} 
                                    draggableId={item.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`
                                          p-3 rounded-lg shadow-sm bg-white dark:bg-gray-800
                                          ${item.priority === 'high' ? 'ring-2 ring-red-200 dark:ring-red-900' : ''}
                                          ${snapshot.isDragging ? 'shadow-lg opacity-80' : ''}
                                        `}
                                      >
                                        <div className="space-y-2">
                                          <div className="flex justify-between items-start">
                                            <p className="font-medium text-gray-800 dark:text-white">
                                              {item.description}
                                            </p>
                                            <select
                                              value={item.status}
                                              onChange={(e) => updateActionItemStatus(item.id, e.target.value as ActionItem['status'])}
                                              className="text-xs ml-1 px-1 py-0.5 rounded border bg-gray-50 dark:bg-gray-700"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <option value="not-started">Not Started</option>
                                              <option value="in-progress">In Progress</option>
                                              <option value="blocked">Blocked</option>
                                              <option value="completed">Completed</option>
                                            </select>
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {item.priority && (
                                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                item.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                                item.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                              }`}>
                                                {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                                              </span>
                                            )}
                                            {item.owner && (
                                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                {item.owner}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex flex-wrap justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                                            {item.dueDate && (
                                              <span>Due: {new Date(item.dueDate).toLocaleDateString()}</span>
                                            )}
                                            <button
                                              onClick={() => deleteActionItem(item.id)}
                                              className="text-red-500 hover:text-red-700"
                                              title="Delete"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                      
                      {/* Completed Column */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col h-full">
                        <div className="flex items-center justify-between mb-3 pb-2 border-b dark:border-gray-600">
                          <h4 className="font-medium flex items-center">
                            <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                            Completed
                            <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                              {filteredActionItems.filter(item => item.status === 'completed').length}
                            </span>
                          </h4>
                        </div>
                        <Droppable droppableId="completed">
                          {(provided) => (
                            <div 
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="space-y-3 flex-1 overflow-y-auto"
                            >
                              {filteredActionItems
                                .filter(item => item.status === 'completed')
                                .map((item, index) => (
                                  <Draggable 
                                    key={item.id} 
                                    draggableId={item.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`
                                          p-3 rounded-lg shadow-sm bg-white dark:bg-gray-800 opacity-80
                                          ${snapshot.isDragging ? 'shadow-lg opacity-70' : ''}
                                        `}
                                      >
                                        <div className="space-y-2">
                                          <div className="flex justify-between items-start">
                                            <p className="font-medium text-gray-800 dark:text-white line-through">
                                              {item.description}
                                            </p>
                                            <select
                                              value={item.status}
                                              onChange={(e) => updateActionItemStatus(item.id, e.target.value as ActionItem['status'])}
                                              className="text-xs ml-1 px-1 py-0.5 rounded border bg-gray-50 dark:bg-gray-700"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <option value="not-started">Not Started</option>
                                              <option value="in-progress">In Progress</option>
                                              <option value="blocked">Blocked</option>
                                              <option value="completed">Completed</option>
                                            </select>
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {item.priority && (
                                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                                {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                                              </span>
                                            )}
                                            {item.owner && (
                                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                                {item.owner}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex flex-wrap justify-between items-center text-xs text-gray-400 dark:text-gray-500">
                                            {item.dueDate && (
                                              <span>Due: {new Date(item.dueDate).toLocaleDateString()}</span>
                                            )}
                                            <button
                                              onClick={() => deleteActionItem(item.id)}
                                              className="text-red-400 hover:text-red-600"
                                              title="Delete"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    </div>
                  </DragDropContext>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgileTools;